import { oneOf, shape, string } from "prop-types";
import React from "react";
import { getFullName } from "utils/getFullName";
import { toInitials } from "utils/toInitials";

import styles from "./UserAvatar.module.scss";

export const UserAvatarSize = {
  md: "md",
  lg: "lg",
};

const SIZE_CLASSES = {
  [UserAvatarSize.md]: styles.md,
  [UserAvatarSize.lg]: styles.lg,
};

const AVATAR_CLASSES = [
  styles.purple,
  styles.blue,
  styles.green,
  styles.orange,
  styles.pink,
  styles.teal,
];

const AVATAR_COLORS = [
  "var(--token-surface-primary-600)",
  "var(--token-surface-information-600)",
  "var(--token-border-success-700)",
  "var(--token-surface-warning-700)",
  "var(--token-surface-error-600)",
  "var(--token-surface-gray-600)",
];

const hashToColorIndex = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.trunc(h * 31 + str.codePointAt(i));
  }
  return Math.abs(h) % AVATAR_CLASSES.length;
};

export const getAvatarColor = (initials) =>
  AVATAR_COLORS[hashToColorIndex(initials)];

const UserAvatar = ({
  initials,
  user,
  name,
  className,
  size = UserAvatarSize.md,
}) => {
  const resolvedName = name ?? (user ? getFullName(user) : undefined);

  const resolvedInitials =
    initials ?? toInitials(getFullName(user), user?.userName);

  const colorClass = AVATAR_CLASSES[hashToColorIndex(resolvedInitials)];

  return (
    <span
      className={`${styles.avatar} ${SIZE_CLASSES[size]} ${colorClass} ${
        className ?? ""
      }`.trim()}
      data-testid="user-avatar"
      title={resolvedName}
    >
      {resolvedInitials}
    </span>
  );
};

const requireInitialsOrUser = (props, _, componentName) =>
  props.initials === undefined && props.user === undefined
    ? new Error(
        `One of "initials" or "user" is required in "${componentName}", but neither was supplied.`
      )
    : null;

UserAvatar.propTypes = {
  initials: requireInitialsOrUser,
  user: shape({
    userName: string,
    firstName: string,
    lastName: string,
  }),
  name: string,
  className: string,
  size: oneOf(Object.values(UserAvatarSize)),
};

export default UserAvatar;
